import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ICustomTable } from '@/types/customTable.types'

import CustomView from '../CustomView'
import TableBody from './TableBody'
import TableFooter from './TableFooter'
import TableHead from './TableHead'
import TableTools from './TableTools'
import NewProductPreparation from '@/pages/Preparation/components/new/NewProduct'
import NewBreakdownPreparation from '@/pages/Preparation/components/new/NewBreakdown'
import NewServicePreparation from '@/pages/Preparation/components/new/NewService'
import DetailView from '@/pages/Profile/components/DetailView'
import { PATHS } from '@/utils/paths'
import { useTableData } from '@/hooks/useTableData'
import { CONSTANTS } from '@/utils/CONSTANTS'

function CustomTable({
  columns,
  dataset,
  totalCount = 0,
  selectedRows = [],
  actions,
  newDataComponent,
  formFilter,
  handleUpdatePatio,
  handleExport,
  handleReprove,
  handleApprove,
  handleActivate,
  handleInactivate,
  handleAction,
  handleBatchOfReport,
  handleCleanFilters,
  handleManagerAnalysis,
  handleIntegration,
  handleMargin,
  hasFilters,
  actionCancel,
  showSelectColumn,
  canChoiceOnlyOneSelect,
  selectionMatchFields,
  selectionConstraintMessage,
  showTableTools = true,
  showTableFooter = true,
  rowDisabled,
  hideColumnManager = false,
  clearCheckAutomatic = false,
  customTableTools,
  enableSort = true,
  overrideAction = false,
  allowEditInactive = false,
  disableSearchParams = false,
  pendingManagerReviewSelected = 0,
  handlePermissions
}: ICustomTable) {
  const { components } = CONSTANTS
  const [filter, setFilter] = useState(dataset)
  const [createNewData, setCreateNewData] = useState(false)
  const [newColumns, setNewColumns] = useState(columns)
  const [newColumnsId, setNewColumnsId] = useState<number[]>([])
  const {
    CLEAR_CHECK_SELECTS,
    CLEAR_PAGINATION_AND_ORDINATION,
    SET_ACTUAL_COMPONENT
  } = useTableData()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    setFilter(dataset)

    if (clearCheckAutomatic) CLEAR_CHECK_SELECTS()
  }, [dataset])

  useEffect(() => {
    const arrayTemp = []
    for (let i = 0; i < newColumns.length; i++) {
      arrayTemp.push(i)
    }
    setNewColumnsId(arrayTemp)

    return () => {
      CLEAR_CHECK_SELECTS()
    }
  }, [])

  const handleReturnToComponent = (component: any) => {
    if (component) SET_ACTUAL_COMPONENT(component)
    setCreateNewData(false)
  }

  // Função que navega para a página de criação de um novo item com base no tipo de componente especificado
  function navigateToNewItemPage() {
    switch (newDataComponent) {
      case 'breakdown':
        return navigate(PATHS.parametrization.breakdowns.new, {
          state: { from: location.pathname }
        })
      case 'category':
        return navigate(PATHS.parametrization.category.new, {
          state: { from: location.pathname }
        })
      case 'categoriaVistoria':
        return navigate(PATHS.parametrization.inspectionCategories.new, {
          state: { from: location.pathname }
        })
      case 'product':
        return navigate(PATHS.parametrization.products.new, {
          state: { from: location.pathname }
        })
      case 'providers':
        return navigate(PATHS.parametrization.providers.new, {
          state: { from: location.pathname }
        })
      case 'services':
        return navigate(PATHS.parametrization.services.new, {
          state: { from: location.pathname }
        })
      case 'serialItems':
        return navigate(PATHS.parametrization.serialItem.new, {
          state: { from: location.pathname }
        })
      case 'accessories':
        return navigate(PATHS.parametrization.accessories.new, {
          state: { from: location.pathname }
        })
      case 'company':
        return navigate(PATHS.parametrization.appraisalCompany.new, {
          state: { from: location.pathname }
        })
      case 'marks':
        return navigate(PATHS.parametrization.brands.new, {
          state: { from: location.pathname }
        })
      case 'SLA':
        return navigate(PATHS.parametrization.sla.new, {
          state: { from: location.pathname }
        })
      case 'inspection':
        return navigate(PATHS.inspections.new, {
          state: { from: location.pathname }
        })
      case 'refund':
        return navigate(PATHS.refund.new, {
          state: { from: location.pathname }
        })
      case 'profile':
        return <DetailView returnToTable={() => setCreateNewData(false)} /> //TODO: implementar
      case 'precification':
        return navigate(PATHS.precification.new, {
          state: { from: location.pathname }
        })
      case 'users':
        return navigate(PATHS.users.new, { state: { from: location.pathname } })
      case 'preparation':
        return navigate(PATHS.preparation.new, {
          state: { from: location.pathname }
        })
      case 'evaluationType':
        return navigate(PATHS.parametrization.evaluationType.new, {
          state: { from: location.pathname }
        })
      case 'inspectionPhoto':
        return navigate(PATHS.parametrization.inspectionPhotos.new, {
          state: { from: location.pathname }
        })
      case 'photoSection':
        return navigate(PATHS.parametrization.photoSections.new, {
          state: { from: location.pathname }
        })
      case 'photoFlow':
        return navigate(PATHS.parametrization.photoFlow.new, {
          state: { from: location.pathname }
        })
      default:
        return null
    }
  }

  function handleCreateNewData() {
    setCreateNewData(true)
    CLEAR_PAGINATION_AND_ORDINATION()
    navigateToNewItemPage()
  }

  // TODO: Remover quando todas as rotas para criar item estiverem implementadas
  if (
    createNewData &&
    newDataComponent !== 'services-preparation' &&
    newDataComponent !== 'product-preparation' &&
    newDataComponent !== 'breakdown-preparation'
  ) {
    const newItemPage = navigateToNewItemPage()
    if (typeof newItemPage === 'object') {
      return newItemPage
    }
  }

  return (
    <CustomView>
      {createNewData && newDataComponent === 'services-preparation' && (
        <NewServicePreparation returnToTable={() => setCreateNewData(false)} />
      )}
      {createNewData && newDataComponent === 'product-preparation' && (
        <NewProductPreparation returnToTable={() => setCreateNewData(false)} />
      )}
      {createNewData && newDataComponent === 'breakdown-preparation' && (
        <NewBreakdownPreparation
          returnToTable={() => setCreateNewData(false)}
        />
      )}
      {showTableTools && (
        <TableTools
          buttonText={newDataComponent}
          createNewData={handleCreateNewData}
          formFilter={formFilter}
          actionCancel={actionCancel}
          handleUpdatePatio={handleUpdatePatio}
          handleExport={handleExport}
          handleApprove={handleApprove}
          handleReprove={handleReprove}
          handleActivate={handleActivate}
          handleInactivate={handleInactivate}
          handleBatchOfReport={handleBatchOfReport}
          handleCleanFilters={handleCleanFilters}
          handleManagerAnalysis={handleManagerAnalysis}
          handleIntegration={handleIntegration}
          handleMargin={handleMargin}
          handlePermissions={handlePermissions}
          pendingManagerReviewSelected={pendingManagerReviewSelected}
          hasFilters={hasFilters}
          columns={columns}
          setColumns={setNewColumns}
          columnsId={newColumnsId}
          setColumnsId={setNewColumnsId}
          selectedRows={selectedRows}
          hideColumnManager={hideColumnManager}
          customTableTools={customTableTools}
        />
      )}

      <CustomView overflow="auto" backColor="white">
        <CustomView
          display="grid"
          minWidth={494}
          mt={showTableTools ? '32px' : ''}
          overflow="auto"
        >
          <TableHead
            dataset={filter}
            columns={newColumns}
            hasAction={!!actions?.length}
            showSelectColumn={showSelectColumn}
            canChoiceOnlyOneSelect={canChoiceOnlyOneSelect}
            selectionMatchFields={selectionMatchFields}
            enableSort={enableSort}
            disableSearchParams={disableSearchParams}
          />
          <TableBody
            dataset={filter}
            actions={actions ? actions : []}
            columnsId={newColumnsId}
            showSelectColumn={showSelectColumn}
            canChoiceOnlyOneSelect={canChoiceOnlyOneSelect}
            selectionMatchFields={selectionMatchFields}
            selectionConstraintMessage={selectionConstraintMessage}
            handleAction={handleAction}
            overrideAction={overrideAction}
            allowEditInactive={allowEditInactive}
            rowDisabled={rowDisabled}
          />
        </CustomView>
      </CustomView>
      {showTableFooter && (
        <TableFooter
          totalCount={totalCount}
          disableSearchParams={disableSearchParams}
        />
      )}
    </CustomView>
  )
}

export default CustomTable
